import React from 'react';
import { FormattedMessage } from '@kyruus/intl';
import styled from '@emotion/styled';
import Snackbar, { Button as SnackbarButton } from '@kyruus/snackbar';
import { fromTheme } from '@kyruus/ui-theme';

import { Box } from '@biome-design-system/ui/Box';
import { visuallyHidden } from '@biome-design-system/styles';
import ProviderList, { FROM_SEARCH_MAP } from 'Src/shared/ProviderListV9';
import { ResultsHeader } from 'Src/shared/ResultsHeader';
import LoadMoreButton from 'Src/shared/LoadMoreButton';
import LoadingOverlay from 'Src/shared/loading';
import MapCard from '../MapCard';
import { SideSheet } from 'Src/shared/SideSheet';

import { messages } from '../../lib/messages';
import { MapsNoResultsBanner, MapsErrorBanner } from '../ErrorBanner';
import {
  PROVIDER_DRAWER_OPEN,
  RENDER_PROVIDERS,
  FETCHING_PROVIDERS,
  LOADING_MORE_PROVIDERS,
  RENDER_SINGLE_LOCATION,
  NO_RESULTS,
  ERROR,
  IDLE,
  SNACKBAR,
  SHOW
} from '../../../machines/map-search/map-search-states';
import {
  CLOSE_PROVIDER_DRAWER,
  LOAD_MORE_PROVIDERS,
  CLOSE_SNACKBAR
} from '../../../machines/map-search/map-search-actions';
import {
  getProvidersFromV9Results,
  getSlots,
  getTotalProvidersFromV9Results
} from '../../../behaviors/search-map-v9';
import { getRelativeParameterizedBookingUrl } from '../../../utils/getRelativeParameterizedBookingUrl';
import {
  CLICK_CLOSE_PROVIDER_DRAWER,
  CLICK_LOAD_MORE_PROVIDERS,
  HIDE_HOW_TO_PIN,
  CLICK_MAPS_PROVIDER_TILE,
  CLICK_MAPS_PROVIDER_TILE_BOOK_APPT
} from '../../lib/constants';
import {
  ProviderDrawerContainer,
  ProviderDrawerContentWrapper
} from './styles';
import useKeyboardAccessibilityFocus from '../../hooks/keyboard-accessibility-focus';
import { getApptOptions } from '../../../utils/map-search';
import { gMapLatLngForMloc } from '../../lib/utils';
import { useKeydownListener } from '../../../utils/hooks';

const AbsoluteSnackBar = styled(Snackbar)`
  position: absolute;
  & .MuiPaper-root {
    color: ${fromTheme('color_text_contrast')};
  }
`;

function LargeView({
  intl,
  state,
  send,
  onLocationLinkClick,
  currentQuery,
  config,
  searchSummary,
  sortOptions,
  getUpdatedLocations,
  searchParams,
  log,
  location,
  customerCode,
  productName,
  gmapRef,
  slotsByProviderId,
  slotsStatusByProviderId,
  fetchPurposesByProviderId,
  fetchProviderAvailability,
  purposeOptionsByProviderId,
  availabilityError,
  purposesLoading
}) {
  const { providersForDrawer, hasMoreProvidersToLoad, selectedLocation } =
    state.context;

  const isDrawerInLoadingState =
    state.matches(`${PROVIDER_DRAWER_OPEN}.${FETCHING_PROVIDERS}`) ||
    state.matches(`${PROVIDER_DRAWER_OPEN}.${LOADING_MORE_PROVIDERS}`);

  // autofocus the close button when the drawer is opened
  // (so keyboard tabbing enters the drawer)
  const { focusPin } = useKeyboardAccessibilityFocus(
    null,
    state,
    isDrawerInLoadingState
  );

  const onCloseProviderDrawerClick = () => {
    log(CLICK_CLOSE_PROVIDER_DRAWER);

    // recenter map to pin
    if (gmapRef.current) {
      gmapRef.current.panTo(gMapLatLngForMloc(selectedLocation));
    }
    // put focus back onto map pin (for kb accessibility)
    focusPin();

    send(CLOSE_PROVIDER_DRAWER);
  };

  const onLoadMoreProvidersClick = () => {
    log(CLICK_LOAD_MORE_PROVIDERS);
    send(LOAD_MORE_PROVIDERS);
  };

  // close drawer on escape key
  useKeydownListener('Escape', () => {
    if (state.matches(`${PROVIDER_DRAWER_OPEN}`) && !isDrawerInLoadingState) {
      onCloseProviderDrawerClick();
    }
  });

  const closeSnackbar = () => {
    log(HIDE_HOW_TO_PIN);
    send(CLOSE_SNACKBAR);
  };

  const providers = getProvidersFromV9Results(providersForDrawer) ?? [];
  const { slots, slotsStatus } = getSlots(
    providers,
    slotsByProviderId,
    slotsStatusByProviderId
  );

  let drawerContent = null;
  let drawerHeader = null;

  // drawer state for bookable location/urgent care clinic
  if (state.matches(`${PROVIDER_DRAWER_OPEN}.${RENDER_SINGLE_LOCATION}`)) {
    drawerHeader = selectedLocation.name;
    drawerContent = (
      <React.Fragment>
        <MapCard
          locationData={selectedLocation}
          currentQuery={currentQuery}
          ctaText={intl.formatMessage(messages.bookAppointmentOnline)}
          bookOnlineUrl={getRelativeParameterizedBookingUrl({
            provider: providers[0],
            config,
            location
          })}
          onMobileCancelHandler={onCloseProviderDrawerClick}
          onLocationClick={onLocationLinkClick}
        />
      </React.Fragment>
    );
  }

  // drawer state for lists of providers at a location
  // display current contents during load more (loading overlay is in parent)
  if (
    state.matches(`${PROVIDER_DRAWER_OPEN}.${LOADING_MORE_PROVIDERS}`) ||
    state.matches(`${PROVIDER_DRAWER_OPEN}.${RENDER_PROVIDERS}`)
  ) {
    drawerHeader = selectedLocation.name;
    drawerContent = (
      <React.Fragment>
        <MapCard
          locationData={selectedLocation}
          currentQuery={currentQuery}
          onMobileCancelHandler={onCloseProviderDrawerClick}
          onLocationClick={onLocationLinkClick}
        />

        <ResultsHeader
          config={config}
          getUpdatedSearch={getUpdatedLocations}
          locationName={selectedLocation.name}
          log={log}
          searchSummary={searchParams}
          sortOptions={sortOptions}
          sx={{ marginY: 2 }}
          totalProviders={getTotalProvidersFromV9Results(providersForDrawer)}
        />

        <ProviderList
          providers={providers}
          customerCode={customerCode}
          productName={productName}
          config={config}
          searchSummary={searchSummary}
          log={log}
          shouldAppendAnalyticsParamsBookOnlineUrl={true}
          profileSummaryParams={{
            from: FROM_SEARCH_MAP,
            location_id: selectedLocation.id
          }}
          providerCallbackFunc={(provider) =>
            log(CLICK_MAPS_PROVIDER_TILE, { provider_id: provider.id })
          }
          bookingCallbackFunc={(provider) =>
            log(CLICK_MAPS_PROVIDER_TILE_BOOK_APPT, {
              provider_id: provider.id
            })
          }
          apptOptions={getApptOptions(
            providersForDrawer._result,
            selectedLocation.id
          )}
          slots={slots}
          slotsStatus={slotsStatus}
          fetchPurposesByProviderId={fetchPurposesByProviderId}
          fetchProviderAvailability={fetchProviderAvailability}
          purposeOptionsByProviderId={purposeOptionsByProviderId}
          availabilityError={availabilityError}
          purposesLoading={purposesLoading}
        />
        {hasMoreProvidersToLoad && (
          <LoadMoreButton onLoadMoreProvidersClick={onLoadMoreProvidersClick} />
        )}
      </React.Fragment>
    );
  }

  if (state.matches(`${PROVIDER_DRAWER_OPEN}.${NO_RESULTS}`)) {
    drawerHeader = intl.formatMessage(messages.noResultsMapsProviderErrorTitle);
    drawerContent = (
      <MapsNoResultsBanner
        messages={messages}
        showBackToSearchResultLink={false}
        log={log}
      />
    );
  }

  if (state.matches(`${PROVIDER_DRAWER_OPEN}.${ERROR}`)) {
    drawerHeader = intl.formatMessage(
      messages.loadingErrorMapsProviderErrorTitle
    );
    drawerContent = <MapsErrorBanner messages={messages} log={log} />;
  }

  return (
    <React.Fragment>
      {state.matches(PROVIDER_DRAWER_OPEN) && (
        <SideSheet
          ariaLabelledBy="provider-side-sheet-header"
          dataTestId="SideSheet"
          anchor="left"
          variant="persistent"
          width="60vw"
          onClose={() => onCloseProviderDrawerClick()}
          header={
            <Box>
              <Box id="provider-side-sheet-header" sx={visuallyHidden}>
                {drawerHeader}
              </Box>
            </Box>
          }
        >
          <ProviderDrawerContainer>
            <LoadingOverlay loading={isDrawerInLoadingState} />
            <ProviderDrawerContentWrapper data-testid="ProviderDrawerContent">
              {drawerContent}
            </ProviderDrawerContentWrapper>
          </ProviderDrawerContainer>
        </SideSheet>
      )}
      <AbsoluteSnackBar
        data-testid="MapSnackbar"
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center'
        }}
        open={state.matches({
          [IDLE]: {
            [SNACKBAR]: SHOW
          }
        })}
        message={intl.formatMessage(messages.mapSnackbarMessage)}
        action={
          <SnackbarButton
            data-testid="MapSnackbarCloseButton"
            onClick={closeSnackbar}
            key="close"
            color="inherit"
          >
            <FormattedMessage {...messages.mapSnackbarButton} />
          </SnackbarButton>
        }
      ></AbsoluteSnackBar>
    </React.Fragment>
  );
}

export default LargeView;
